import React, { Component, Fragment, useEffect, useState } from "react";
import { useMediaQuery } from "@react-hook/media-query";
import { useMeasure } from "react-use";
import { Toaster } from "react-hot-toast";
import I18n from "i18n-js";

import { getDashboardData } from "../services/dashboard";

import { PROGRAM_ADVISOR, FullAccessRoles, NotCoreLogicRoles } from "../config/roles";

import useRoutingState from "../hooks/useRoutingState";

import Card from "./components/Card";
import DashboardSelector from "./components/DashboardSelector";
import HealthSection from "./components/HealthSection";
import OverallHealth from "./components/OverallHealth";
import TeamStage from "./components/TeamStage";
import TeamHealth from "./components/TeamHealth";
import SelfReportedTeamMetrics from "./components/SelfReportedTeamMetrics";
import LeadershipHealth from "./components/LeadershipHealth";
import DevelopmentPlan from "./components/DevelopmentPlan";
import EditTeamButton from "./components/actions/EditTeamButton";
import AddMembersButton from "./components/actions/AddMembersButton";
import InsightsButton from "./components/actions/InsightsButton";

import Blur from "../commons/Blur";
import ChatMessage from "../Chat/ChatMessage";

const DashboardPlacement = {
  DEFAULT: 0,                       // Normal members see this placement
  BASIC: 1,                         // Leaders with no access to leadership KPIs and My Health (not being used)
  LEADERSHIP: 2,                    // Complete leader dashboard, show leadership KPIs and leader's My Health
  LEADERSHIP_WITHOUT_MY_HEALTH: 3   // Leader dashboard without My Health, for non Core Logic Administrative roles
};

const Dashboard = ({ user, team, teams, isTeamLeadOrAbove, data }) => {
  useRoutingState();

  const { 
    user_role,
    user_health,
    team_health,
    ths,
    leader_health,
    members_healths, 
    team_stage, 
    development_plan
  } = data;

  const showTopChat = useMediaQuery("screen and (max-width: 1535px)");

  const [dashboardCardsRef, dashboardCardsMeasure] = useMeasure();

  const [placement, setPlacement] = useState();
  const [chatExpanded, setChatExpanded] = useState(false);

  const dashboard = getDashboardData(user_health, team_health);

  useEffect(() => {
    const setDashboardPlacement = () => {
      let placement = DashboardPlacement.DEFAULT;

      if (canAccessLeadershipMetrics()) {
        if (canAccessMyHealth()) {
          placement = DashboardPlacement.LEADERSHIP;
        } else {
          placement = DashboardPlacement.LEADERSHIP_WITHOUT_MY_HEALTH;
        }
      }

      setPlacement(placement);
    };

    setDashboardPlacement();
  }, []);

  const canAccessTeamMemberDetails = () => {
    if (user_role === "team_lead" &&
        !team.team_lead_can_access_leadership_data) {
      return false;
    }

    return FullAccessRoles.includes(user_role);
  }

  const canAccessLeadershipMetrics = () => {
    return FullAccessRoles.includes(user_role);
  }

  const canAccessMyHealth = () => {
    return !NotCoreLogicRoles.includes(user_role);
  }

  const canAccessTeamPage = () => {
    return FullAccessRoles.includes(user_role);
  }

  const canViewInsights = () => {
    return user_role === PROGRAM_ADVISOR;
  }

  const handleChatExpandClick = () => {
    setChatExpanded(!chatExpanded);
  }

  const renderDashboardSelector = () => (
    <div className="dashboard-row selector">
      <DashboardSelector
        user={user}
        teams={teams}
        selectedTeam={team}
        isTeamLeadOrAbove={isTeamLeadOrAbove}
      />
      <div className="action-buttons">
        {canAccessTeamPage() ? (
          <Fragment>
            <AddMembersButton loggedUser={user} team={team} />
            <EditTeamButton team={team} />
          </Fragment>
        ) : null}
        {canViewInsights() ? <InsightsButton team={team} /> : null}
      </div>
    </div>
  );

  const renderFirstRow = () => {
    if (chatExpanded) return null;
    return (
      <div className="dashboard-row">
        <OverallHealth {...ths} />
        <TeamStage {...team_stage} />
        <DevelopmentPlan plan={development_plan} ofuscate={false} />
      </div>
    );
  }

  const renderSecondRow = () => {
    if (chatExpanded) return null;
    return (
      <div className="dashboard-row">
        <TeamHealth
          {...team_health}
          showMemberDetails={canAccessTeamMemberDetails()}
          membersHealths={members_healths}
        />
      </div>
    );
  }
 
  const renderThirdRow = () => {
    if (chatExpanded) return null;
    
    let panels = [];

    switch (placement) {
      case DashboardPlacement.DEFAULT:
      case DashboardPlacement.LEADERSHIP:
        panels = [
          <HealthSection 
            key="my-health-section"
            {...dashboard.my_health}
          />
        ];
        break;
      case DashboardPlacement.BASIC:
        panels = [
          <SelfReportedTeamMetrics
            key="self-reported-team-metrics"
            team={team}
            confidenceLevel={team_health.confidence_level}
            resources={team_health.resources}
            clarityOfGoals={team_health.clarity_goals_roles}
          />
        ];
        break;
      case DashboardPlacement.LEADERSHIP_WITHOUT_MY_HEALTH:
        panels = [
          <SelfReportedTeamMetrics
            key="self-reported-team-metrics"
            team={team}
            confidenceLevel={team_health.confidence_level}
            resources={team_health.resources}
            clarityOfGoals={team_health.clarity_goals_roles}
          />,
          <LeadershipHealth
            key="leadership-health"
            trustEnvironment={leader_health?.trust_environment}
            managementPractice={leader_health?.management_practice}
          />
        ];
        break;
      default:
        break;
    }

    return panels.length ? <div className="dashboard-row">{panels}</div> : null;
  }

  const renderFourthRow = () => {
    if (chatExpanded) return null;

    let panels = [];

    switch (placement) {
      case DashboardPlacement.DEFAULT:
        panels = [
          <SelfReportedTeamMetrics
            key="self-reported-team-metrics"
            team={team}
            confidenceLevel={team_health.confidence_level}
            resources={team_health.resources}
            clarityOfGoals={team_health.clarity_goals_roles}
          />
        ];
        break;
      case DashboardPlacement.LEADERSHIP:
        panels = [
          <SelfReportedTeamMetrics
            key="self-reported-team-metrics"
            team={team}
            confidenceLevel={team_health.confidence_level}
            resources={team_health.resources}
            clarityOfGoals={team_health.clarity_goals_roles}
          />,
          <LeadershipHealth
            key="leadership-health"
            trustEnvironment={leader_health?.trust_environment}
            managementPractice={leader_health?.management_practice}
          />
        ];
        break;
      default:
        break;
    }

    return panels.length ? <div className="dashboard-row">{panels}</div> : null;
  }

  const renderSidePanelWhenChatExpanded = () => {
    if (!chatExpanded) return null;
    return (
      <div className="dashboard-row">
        {canAccessMyHealth() ? (
          <HealthSection {...dashboard.my_health} />
        ) : (
          <TeamHealth
            {...team_health}
            showMemberDetails={canAccessTeamMemberDetails()}
            membersHealths={members_healths}
          />
        )}
      </div>
    );
  }

  const renderChatSection = (top = false) => {
    let className = top ? "dashboard-top-chat" : "dashboard-chat";
    className = chatExpanded ? className + " chat-expanded" : className;
    return (
      <div className={className} style={{ maxHeight: dashboardCardsMeasure.height }}>
        <ChatMessage
          user={user}
          team={team}
          expand={!top}
          showSuggestions
          onChatExpandClick={handleChatExpandClick}
        />
      </div>
    );
  }

  const renderDashboardNotReadyWarning = () => (
    <div className="dashboard">
      <div className="container-fluid">
        {renderDashboardSelector()}
        <div className="dashboard-warning">
          <Card content={I18n.t("dashboard.dashboard_not_ready")} />
        </div>
      </div>
    </div>
  );

  if (!team.can_show_dashboard) {
    return renderDashboardNotReadyWarning();
  }
    
  return (
    <Fragment>
      <div className="wrapper">
        <div className={chatExpanded ? "dashboard chat-expanded" : "dashboard"}>
          <div className="container-fluid">
            {renderDashboardSelector()}
            {showTopChat ? renderChatSection(true) : null}
            <div className={chatExpanded ? "dashboard-content chat-expanded" : "dashboard-content"}>
              <div
                className={chatExpanded ? "dashboard-cards chat-expanded" : "dashboard-cards"}
                ref={dashboardCardsRef}
              >
                {renderFirstRow()}
                {renderSecondRow()}
                {renderThirdRow()}
                {renderFourthRow()}
                {renderSidePanelWhenChatExpanded()}
              </div>
              {!showTopChat ? renderChatSection() : null}
            </div>
          </div>
        </div>
        <Blur />
      </div>
      <Toaster
        position="top-right"
        reverseOrder
      />
    </Fragment>
  );
}

// NOTE: needed now to current version of ReactOnRails to work with hooks
export default class DashboardComponent extends Component {
  render() {
    return <Dashboard {...this.props} />;
  }
}